import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  Shield, Users, CheckCircle, FileText, Link2, Clock, 
  Fingerprint, Lock, Award, ArrowRight, Check, Cpu, 
  Database, Scan, Globe, Usb, Building2, MapPin,
  Briefcase, MessageCircle, X, Send, ChevronRight
} from "lucide-react";

// Original FingerprintVS Logo URLs
const LOGO_MAIN = "https://static.prod-images.emergentagent.com/jobs/8bf96fc3-b1b9-4153-935e-e0d5c02560cb/images/ec004e5775aa2226d0fa17cb62a615fe36de1a6fc64cd1e08d5a79ec72845ee0.png";
const LOGO_HEADER = "https://static.prod-images.emergentagent.com/jobs/8bf96fc3-b1b9-4153-935e-e0d5c02560cb/images/106cc6e384596d83e239ead5e79655a5c801b307beb84c6e0ce0005077375f78.png";
const LOGO_ICON = "https://static.prod-images.emergentagent.com/jobs/8bf96fc3-b1b9-4153-935e-e0d5c02560cb/images/b5ffd65d20497bfd9681416db86fdb9eea50ae3581cd0d0b7ff660d2e2271341.png";

// Chatbot Component
const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Hi! 👋 Welcome to FingerprintVS. I can help direct you to the right place. Are you:",
      options: [
        { label: "Looking for fingerprinting services", action: 'client' },
        { label: "A business interested in our platform", action: 'vendor' }
      ]
    }
  ]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleOptionClick = (action) => {
    if (action === 'client') {
      setMessages(prev => [...prev, 
        { type: 'user', text: "Looking for fingerprinting services" },
        { 
          type: 'bot', 
          text: "Great! I can help you find a provider or learn about our services.",
          options: [
            { label: "Find a provider near me", link: '/find-provider' },
            { label: "What services do you offer?", link: '/services' },
            { label: "What to bring to my appointment", action: 'appointment' }
          ]
        }
      ]);
    } else if (action === 'vendor') {
      setMessages(prev => [...prev, 
        { type: 'user', text: "A business interested in your platform" },
        { 
          type: 'bot', 
          text: "Excellent! FingerprintVS helps vendors provide professional fingerprinting services. What would you like to know?",
          options: [
            { label: "View pricing plans", link: '/pricing' },
            { label: "Calculate my potential profit", link: '/calculator' },
            { label: "Start free trial", link: '/register' },
            { label: "Learn more about features", link: '/for-vendors' }
          ]
        }
      ]);
    } else if (action === 'appointment') {
      setMessages(prev => [...prev, 
        { type: 'user', text: "What to bring to my appointment" },
        { 
          type: 'bot', 
          text: "For your fingerprinting appointment, please bring:\n\n✓ Valid government-issued photo ID (Driver's License, Passport, or State ID)\n✓ Any forms from your employer/agency\n✓ Payment method (check with provider)\n\nTip: Avoid using hand lotion before your appointment for best print quality.",
          options: [
            { label: "Find a provider", link: '/find-provider' },
            { label: "Back to main menu", action: 'restart' }
          ]
        }
      ]);
    } else if (action === 'restart') {
      setMessages([{
        type: 'bot',
        text: "Hi! 👋 How can I help you today?",
        options: [
          { label: "Looking for fingerprinting services", action: 'client' },
          { label: "A business interested in our platform", action: 'vendor' }
        ]
      }]);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-blue-500 hover:bg-blue-600 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        data-testid="chatbot-toggle"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-80 md:w-96 bg-slate-800 rounded-xl shadow-2xl border border-white/10 overflow-hidden" data-testid="chatbot-window">
          {/* Header */}
          <div className="bg-blue-500 px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Fingerprint className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">FingerprintVS Assistant</p>
              <p className="text-blue-100 text-xs">We typically reply instantly</p>
            </div>
          </div>

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx}>
                {msg.type === 'bot' ? (
                  <div className="flex gap-2">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Fingerprint className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-gray-300 text-sm whitespace-pre-line">{msg.text}</p>
                      {msg.options && (
                        <div className="mt-3 space-y-2">
                          {msg.options.map((opt, i) => (
                            opt.link ? (
                              <Link
                                key={i}
                                to={opt.link}
                                className="block px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm text-white transition"
                                onClick={() => setIsOpen(false)}
                              >
                                {opt.label} →
                              </Link>
                            ) : (
                              <button
                                key={i}
                                onClick={() => handleOptionClick(opt.action)}
                                className="block w-full text-left px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm text-white transition"
                              >
                                {opt.label}
                              </button>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <p className="bg-blue-500 text-white text-sm px-3 py-2 rounded-lg max-w-[80%]">
                      {msg.text}
                    </p>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}
    </>
  );
};

// Grid Pattern Background
const GridPattern = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div 
      className="absolute inset-0 opacity-[0.03]"
      style={{
        backgroundImage: `
          linear-gradient(rgba(34, 211, 238, 0.3) 1px, transparent 1px),
          linear-gradient(90deg, rgba(34, 211, 238, 0.3) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px'
      }}
    />
  </div>
);

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden">
      {/* Main Background Image */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/07e51985-c374-4472-a4ec-7832a182bb43/images/a599bfb20d8b730453411fb0496e41df29ac3936a599b1146f16c28f94161081.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      />
      {/* Dark overlay */}
      <div className="fixed inset-0 z-0 bg-slate-900/80" />
      
      {/* Background Effects */}
      <GridPattern />

      {/* Header */}
      <header className="relative bg-black/20 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img 
              src={LOGO_ICON} 
              alt="FingerprintVS Logo" 
              className="w-10 h-10 rounded-lg"
            />
            <div>
              <span className="text-xl font-bold text-white">FingerprintVS</span>
              <span className="hidden sm:inline text-xs text-cyan-400/70 ml-2">Fingerprinting Virtual System</span>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/services" className="text-gray-300 hover:text-white transition">Services</Link>
            <Link to="/find-provider" className="text-gray-300 hover:text-white transition">Find Provider</Link>
            <Link to="/for-vendors" className="text-gray-300 hover:text-white transition">For Vendors</Link>
            <Link to="/login" className="text-gray-300 hover:text-white transition">Login</Link>
            <Link to="/register" className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition">
              Get Started
            </Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <div className="relative z-10">
        {/* Hero Section with B2B/B2C Split */}
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              FBI/CJIS Compliant Fingerprinting System
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Fingerprinting<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                Made Simple
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Whether you need fingerprints taken or want to provide fingerprinting services — 
              FingerprintVS has you covered.
            </p>
          </div>

          {/* Two Path Cards */}
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
            {/* For Clients */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-blue-500/50 transition group">
              <div className="w-16 h-16 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition">
                <Users className="w-8 h-8 text-blue-400" />
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-4">
                Need Fingerprints Taken?
              </h2>
              
              <p className="text-gray-400 mb-6">
                Find a certified fingerprinting provider near you. Get professional, 
                FBI-compliant fingerprint cards for employment, licensing, immigration, and more.
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Employment background checks</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Professional licensing</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Immigration & visas</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Volunteer & foster care</span>
                </li>
              </ul>
              
              <div className="flex flex-col gap-3">
                <Link 
                  to="/find-provider"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold transition"
                  data-testid="find-provider-cta"
                >
                  <MapPin className="w-5 h-5" />
                  Find a Provider Near Me
                </Link>
                <Link 
                  to="/services"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition"
                >
                  Learn About Services
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* For Vendors */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-purple-500/50 transition group">
              <div className="w-16 h-16 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition">
                <Building2 className="w-8 h-8 text-purple-400" />
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-4">
                Want to Provide Services?
              </h2>
              
              <p className="text-gray-400 mb-6">
                Start or grow your fingerprinting business with our professional platform. 
                Replace expensive LiveScan systems — starting at just $49/month.
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>FBI-compliant card generation</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Cloud, USB, or LAN deployment</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>ID verification with OCR</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Get listed in our directory</span>
                </li>
              </ul>
              
              <div className="flex flex-col gap-3">
                <Link 
                  to="/register"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-semibold transition"
                  data-testid="vendor-cta"
                >
                  Start 14-Day Free Trial
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/for-vendors"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition"
                >
                  Learn About Platform
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto bg-slate-900/50 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-white mb-1">500+</div>
                <div className="text-gray-300 text-sm">Providers Nationwide</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white mb-1">50,000+</div>
                <div className="text-gray-300 text-sm">Clients Served</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white mb-1">100%</div>
                <div className="text-gray-300 text-sm">FBI Compliant</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white mb-1">4.9★</div>
                <div className="text-gray-300 text-sm">Average Rating</div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works - Clients */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto bg-slate-900/50 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <h2 className="text-3xl font-bold text-white text-center mb-4">
              How It Works for Clients
            </h2>
            <p className="text-gray-200 text-center mb-12">
              Getting your fingerprints taken is simple
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">1. Find a Provider</h3>
                <p className="text-gray-200 text-sm">Search our directory for certified providers in your area.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">2. Schedule & Visit</h3>
                <p className="text-gray-200 text-sm">Book your appointment and bring valid ID.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">3. Get Your Cards</h3>
                <p className="text-gray-200 text-sm">Receive FBI-compliant fingerprint cards same day.</p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works - Vendors */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto bg-slate-900/50 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <h2 className="text-3xl font-bold text-white text-center mb-4">
              How It Works for Vendors
            </h2>
            <p className="text-gray-200 text-center mb-12">
              Start your fingerprinting business in minutes
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-400">1</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Sign Up</h3>
                <p className="text-gray-200 text-sm">Create your account and start your free trial.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-400">2</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Connect Scanner</h3>
                <p className="text-gray-200 text-sm">Download our app and connect your Suprema scanner.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-400">3</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Start Earning</h3>
                <p className="text-gray-200 text-sm">Capture fingerprints, generate cards, get paid.</p>
              </div>
            </div>
            
            <div className="text-center mt-12">
              <Link 
                to="/for-vendors"
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-semibold transition"
              >
                Learn More About Becoming a Provider
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-900/50 to-purple-900/50 backdrop-blur-sm rounded-2xl p-12 border border-white/20 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-gray-100 text-lg mb-8">
              Whether you need fingerprinting services or want to provide them — we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/find-provider"
                className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold text-lg transition flex items-center justify-center gap-2"
              >
                <MapPin className="w-5 h-5" />
                Find a Provider
              </Link>
              <Link 
                to="/for-vendors"
                className="px-8 py-4 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-semibold text-lg transition flex items-center justify-center gap-2"
              >
                <Building2 className="w-5 h-5" />
                Become a Provider
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Fingerprint className="w-6 h-6 text-cyan-400" />
                  <span className="text-white font-semibold">FingerprintVS</span>
                </div>
                <p className="text-gray-500 text-sm">
                  Professional fingerprinting made simple. FBI/CJIS compliant.
                </p>
              </div>
              
              <div>
                <h4 className="text-white font-semibold mb-4">For Clients</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/services" className="text-gray-400 hover:text-white transition">Services</Link></li>
                  <li><Link to="/find-provider" className="text-gray-400 hover:text-white transition">Find Provider</Link></li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-white font-semibold mb-4">For Vendors</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/for-vendors" className="text-gray-400 hover:text-white transition">Platform</Link></li>
                  <li><Link to="/pricing" className="text-gray-400 hover:text-white transition">Pricing</Link></li>
                  <li><Link to="/calculator" className="text-gray-400 hover:text-white transition">Profit Calculator</Link></li>
                  <li><Link to="/register" className="text-gray-400 hover:text-white transition">Start Free Trial</Link></li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-white font-semibold mb-4">Support</h4>
                <ul className="space-y-2 text-sm">
                  <li><a href="mailto:support@fingerprintvs.com" className="text-gray-400 hover:text-white transition">Contact Us</a></li>
                  <li><Link to="/login" className="text-gray-400 hover:text-white transition">Login</Link></li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-white/10 pt-8 text-center text-gray-500 text-sm">
              © 2026 FingerprintVS. All rights reserved. • Fingerprinting Virtual System
            </div>
          </div>
        </footer>
      </div>

      {/* Chatbot Widget */}
      <ChatbotWidget />
    </div>
  );
};

export default LandingPage;
